import React, { useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { createPortal } from 'react-dom'
import { ModalProps } from '@/types'

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  bare = false,
  className = '',
}) => {
  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    },
    [onClose]
  )

  useEffect(() => {
    if (!isOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.body.style.overflow = prev
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, handleEscape])

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) onClose()
  }

  const sizeClasses = {
    xs: 'max-w-[20rem]',
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
  }

  const modalContent = (
    <AnimatePresence mode="wait">
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-50 bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={handleBackdropClick}
          />
          <div
            className="fixed inset-0 z-50 flex justify-center overflow-y-auto overscroll-contain pointer-events-none"
            onClick={handleBackdropClick}
          >
            <div className="flex min-h-full w-full items-start justify-center p-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-[max(1rem,env(safe-area-inset-top))] sm:items-center pointer-events-none">
              <motion.div
                className={`pointer-events-auto ${sizeClasses[size]} w-full my-auto ${className}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.18 }}
                onClick={(e) => e.stopPropagation()}
              >
                {bare ? (
                  children
                ) : (
                  <div className="bg-white rounded-xl shadow-lg border border-neutral-200 overflow-hidden">
                    {title && (
                      <div className="flex items-center justify-between px-4 pt-4 pb-0">
                        <h2 className="text-base font-semibold text-neutral-900">{title}</h2>
                        <button
                          type="button"
                          onClick={onClose}
                          className="p-1.5 hover:bg-neutral-100 rounded-md transition-colors"
                          aria-label="Close"
                        >
                          <svg className="w-4 h-4 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    )}
                    <div className="p-4 overflow-y-auto max-h-[min(85dvh,32rem)]">{children}</div>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </>
      )}
    </AnimatePresence>
  )

  return createPortal(modalContent, document.body)
}

export default Modal
